import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  inject,
  ViewChild,
  OnInit,
  ChangeDetectorRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { PublicProgramService } from '../../../api/external-web/public-program.service';
import { NoResultsComponent } from '../no-results/no-results.component';
import { CustomRateComponent } from '../right-side-pannel/custom-rate/custom-rate.component';
import { PublicReviewComponent } from '../public-review/public-review.component';
import { ChipFilterComponent } from '../chip-filters/chip-filter.component';
import { RepresentativeDetailsComponent } from '../right-side-pannel/representative-details/representative-details.component';

@Component({
  selector: 'app-search-lumber-rates',
  standalone: true,
  imports: [
    CommonModule,
    NoResultsComponent,
    PublicReviewComponent,
    RepresentativeDetailsComponent,
    CustomRateComponent,
    ChipFilterComponent,
  ],
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  templateUrl: './search-lumber-rates.component.html',
  styleUrls: ['./search-lumber-rates.component.scss'],
})
export class SearchLumberRatesComponent implements OnInit {
  private readonly cdr = inject(ChangeDetectorRef);
  @ViewChild('publicReview') publicReview: PublicReviewComponent;
  private readonly publicProgramService = inject(PublicProgramService);
  public readonly lumberPath = 'assets/images/lumberheader.png';
  public readonly options = ['Lumber', 'Lumber - Panel products'] as const;
  public selectedOption: string | null = this.options[0];
  public originTransloadChips = [];
  public destinationTransloadChips = [];
  public selected = null;
  public rateTypeNameDetails: any[] = [];
  public selectedType = '';
  public publicProgramData = {};
  public readonly propertyType = 'PUBLIC_PROGRAM_CATEGORY';
  public salesManagerDetails = '';
  public render = { origin: 0, destination: 0 };

  ngOnInit(): void {
    this.getTransloadChipsData({ programType: 'Lumber' });
  }

  /** Top-level selection (Lumber vs Panel products) */
  public onSelect(option: string): void {
    this.selectedOption = option;
    this.originTransloadChips = [];
    this.destinationTransloadChips = [];
    this.selected = null;
    this.render = { origin: 0, destination: 0 };
    this.getTransloadChipsData({ programType: option });
  }

  /** Fetch chips data for origin/destination */
  getTransloadChipsData(payload) {
    this.publicProgramService.searchPublicProgramRequest(payload).subscribe({
      next: (response: any) => {
        if (response.length > 0) {
          this.salesManagerDetails = response[0].commodityCategory?.attribute2;
          this.splitPrograms(response);
        }
      },
    });
  }

  private splitPrograms(programs: any): void {
    const unique = this.removeDuplicates(programs);
    const toChip = (p: any) => ({
      ...p,
      id: String(p.id),
      name: this.getCityState(p.transloadVendor),
    });
    const sortAZ = (a: any, b: any) =>
      (a.name ?? '').localeCompare(b.name ?? '', undefined, {
        sensitivity: 'base',
        numeric: true,
      });
    this.originTransloadChips = unique
      .filter((p) => p.moveType === 'ORIGIN')
      .map(toChip)
      .sort(sortAZ);
    this.destinationTransloadChips = unique
      .filter((p) => p.moveType === 'DESTINATION')
      .map(toChip)
      .sort(sortAZ);
    this.applyDefaultSelection();
    this.cdr.markForCheck();
  }

  /** Pick default chip: first Origin if available, else first Destination */
  private applyDefaultSelection(): void {
    const hasOrigin = this.originTransloadChips.length > 0;
    const hasDest = this.destinationTransloadChips.length > 0;
    if (!hasOrigin && !hasDest) {
      this.selected = null;
      this.selectedType = '';
      this.publicProgramData = {};
      this.reload('origin');
      this.reload('destination');
      return;
    }
    if (hasOrigin) {
      const first = this.originTransloadChips[0];
      this.selected = first;
      this.selectedType = 'Origin';
      this.publicProgramData = first;
      this.reload('destination');
    } else {
      const first = this.destinationTransloadChips[0];
      this.selected = first;
      this.selectedType = 'Destination';
      this.publicProgramData = first;
      this.reload('origin');
    }
    this.refreshReviewDebounced();
  }

  private removeDuplicates(array: any) {
    const seen = new Set<number | string>();
    return array.filter((item) => {
      const key = item.id;
      const duplicate = seen.has(key);
      seen.add(key);
      return !duplicate;
    });
  }

  private getCityState(vendor?: { city?: string; state?: string }): string {
    const city = vendor?.city ?? '';
    const state = vendor?.state ?? '';
    return city && state ? `${city}, ${state}` : city || state;
  }

  /** Origin chip selection */
  public onSelectOrigin(item: any | null): void {
    if (!item) {
      this.selected = null;
      this.selectedType = '';
      this.publicProgramData = {};
      this.reload('destination');
      this.refreshReviewDebounced();
      return;
    }
    this.selected = item;
    this.selectedType = 'Origin';
    this.publicProgramData = item;
    this.reload('destination');
    this.refreshReviewDebounced();
  }

  /** Destination chip selection */
  public onSelectDestination(item: any | null): void {
    if (!item) {
      this.selected = null;
      this.selectedType = '';
      this.publicProgramData = {};
      this.reload('origin');
      this.refreshReviewDebounced();
      return;
    }
    this.selected = item;
    this.selectedType = 'Destination';
    this.publicProgramData = item;
    this.reload('origin');
    this.refreshReviewDebounced();
  }

  /** Flip 0/1 to force @if branches to switch -> component instance is recreated */
  private reload(group: 'origin' | 'destination'): void {
    this.render = { ...this.render, [group]: this.render[group] ^ 1 };
    this.cdr.markForCheck();
  }

  /** Keep your existing behavior */
  private refreshReviewDebounced(): void {
    setTimeout(() => {
      if (this.publicReview) {
        this.publicReview.refresh(this.publicProgramData);
        this.cdr.markForCheck();
      }
    }, 400);
    this.cdr.markForCheck();
  }
}
